/**
 * Step card node for React Flow diagram
 * This component is a complex interactive node with multiple sections and state management
 */
import { Handle, Position } from '@xyflow/react';
import { InfoHint, STEP_FIELD_ICONS } from '@bliks-io/ui';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ProcessStepRecording,
  type PopulatedUser,
  type System,
  type Artifact
} from '@bliks-io/contracts';
import { assigneeFieldVisibility } from '@/lib/assigneeFieldVisibility';
import { StepCardUserHeader } from './StepCardUserHeader';
import { DecisionTablePreview } from '@/components/processes/DecisionTablePreview';
import { InputsSection } from '../components/sections/InputsSection';
import { HelpersSection } from '../components/sections/HelpersSection';
import { TimelineSection } from '../components/TimelineSection';
import { useServerSyncedTextField } from '../hooks/useServerSyncedTextField';

export interface StepCardNodeData {
  recording: ProcessStepRecording | null;
  availableUsers: PopulatedUser[];
  availableSystems: System[];
  availableArtifacts: Artifact[];
  onChange: (field: string, value: unknown) => Promise<void>;
  onChangeDebounced: (field: string, value: unknown) => void;
  onFlushPendingChanges: () => Promise<void>;
  onAddInput: () => void;
  onEditInput: (index: number) => void;
  onRemoveInput: (index: number) => void;
  onOpenDecisionTable: () => void;
}

export interface StepCardNodeProps {
  data: StepCardNodeData;
  /**
   * Attached to the section a parent should measure to anchor an outgoing source
   * handle. Which section that is — "I Do" for decisions, "I Create" for tasks —
   * is StepCardNode's policy and stays encapsulated here.
   */
  sourceAnchorRef?: React.Ref<HTMLDivElement>;
}

const StepCardNode = ({ data, sourceAnchorRef }: StepCardNodeProps) => {
  const { t } = useTranslation();
  // Derive active tab from recording data instead of maintaining local state
  const activeTab = data.recording?.actionType === 'decision' ? 'decision' : 'process';

  // Local state for text inputs to ensure immediate UI feedback, kept in step
  // with writes from the server (Bliksy) without interrupting the typist.
  const taskDescription = useServerSyncedTextField(data.recording?.task?.taskDescription || '');
  const output = useServerSyncedTextField(data.recording?.task?.output || '');

  return (
    <div className="nopan relative bg-white rounded-xl border border-normal w-[760px] p-4">
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: 'transparent',
          border: 'none',
          width: '10px',
          height: '10px',
          left: '-5px'
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: 'transparent',
          border: 'none',
          width: '10px',
          height: '10px',
          right: '-5px'
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        style={{
          background: 'transparent',
          border: 'none',
          width: '10px',
          height: '10px',
          bottom: '-5px'
        }}
      />

      {/* User Info Header */}
      <StepCardUserHeader
        recordedBy={data.recording?.recordedBy}
        assignedTo={data.recording?.assignedTo}
        assignedToModel={data.recording?.assignedToModel}
      />

      {/* I Receive Section */}
      <TimelineSection label={t('viralRecording.labels.iReceive')} isFirst contentClassName="p-1">
        {/* Unified Inputs Section */}
        <InputsSection
          recording={data.recording}
          onAddInput={data.onAddInput}
          onEditInput={data.onEditInput}
          onRemoveInput={data.onRemoveInput}
        />
      </TimelineSection>

      {/* I Do Section */}
      <TimelineSection label={t('viralRecording.labels.iDo')} contentClassName="p-1 max-w-[657px]">
        {/* Process Step Description */}
        <div
          ref={activeTab === 'decision' ? sourceAnchorRef : undefined}
          className="bg-bg-1 border border-border-14 rounded-lg p-2"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <STEP_FIELD_ICONS.taskDescription className="w-4 h-4 text-fg-secondary" />
              <span className="text-sm font-semibold text-foreground">
                {activeTab === 'decision'
                  ? t('viralRecording.labels.decisionRules')
                  : t('viralRecording.labels.processStepDescription')}
                <span className="ml-1 text-destructive">*</span>
              </span>
              <InfoHint content={t('viralRecording.tooltips.actionType')} />
            </div>
            <div className="flex items-center gap-1.5">
              <div className="flex gap-1.5 items-center p-1 bg-white border border-line rounded-xl">
                <button
                  onClick={() => {
                    if (data.recording?.actionType !== 'task') {
                      data.onChange('actionType', 'task');
                    }
                  }}
                  className={`relative px-2.5 py-1.5 rounded-lg text-sm font-semibold ${
                    activeTab === 'process' ? 'bg-[#fff9e9] text-black' : 'text-gray-5'
                  }`}
                >
                  {t('viralRecording.labels.process')}
                  {activeTab === 'process' && (
                    <div
                      aria-hidden="true"
                      className="absolute border border-border-7 border-solid inset-0 pointer-events-none rounded-lg"
                    />
                  )}
                </button>
                <button
                  onClick={() => {
                    if (data.recording?.actionType !== 'decision') {
                      data.onChange('actionType', 'decision');
                    }
                  }}
                  className={`relative px-2.5 py-1.5 rounded-lg text-sm font-semibold ${
                    activeTab === 'decision' ? 'bg-[#fff9e9] text-black' : 'text-gray-5'
                  }`}
                >
                  {t('viralRecording.labels.decision')}
                  {activeTab === 'decision' && (
                    <div
                      aria-hidden="true"
                      className="absolute border border-border-7 border-solid inset-0 pointer-events-none rounded-lg"
                    />
                  )}
                </button>
              </div>
            </div>
          </div>
          {activeTab === 'process' ? (
            <textarea
              className="block w-full p-3 m-0 text-sm border border-line rounded-lg resize-none focus:outline-hidden focus:ring-2 focus:ring-info-solid focus:border-transparent"
              rows={4}
              placeholder={t('viralRecording.placeholders.defineProcessStepDescription')}
              ref={taskDescription.attachInput}
              value={taskDescription.value}
              onFocus={taskDescription.handleFocus}
              onBlur={() => {
                taskDescription.handleBlur();
                // Flush the pending write now — its optimistic cache update
                // enables the finish button at blur instead of after the
                // debounce + round trip. Failures re-stage the fields; the next
                // flush retries them, and the field keeps the typed text until
                // one of them lands.
                data
                  .onFlushPendingChanges()
                  .then(taskDescription.markCommitted)
                  .catch(() => {});
              }}
              onChange={(e) => {
                const newValue = e.target.value;
                taskDescription.setValue(newValue); // Immediate UI update
                data.onChangeDebounced('task.taskDescription', newValue); // Debounced server sync
              }}
            />
          ) : (
            <DecisionTablePreview
              rules={data.recording?.rules}
              onOpenDecisionTable={data.onOpenDecisionTable}
            />
          )}
        </div>
      </TimelineSection>

      {/* I Use Section — hidden for System assignees (systems don't use helpers/tools) */}
      {assigneeFieldVisibility(data.recording?.assignedToModel).helpers && (
        <TimelineSection label={t('viralRecording.labels.iUse')} contentClassName="p-1">
          <HelpersSection recording={data.recording} onChange={data.onChange} />
        </TimelineSection>
      )}

      {/* I Create Section - Only shown for task/process type */}
      {activeTab === 'process' && (
        <TimelineSection label={t('viralRecording.labels.iCreate')} contentClassName="p-1">
          <div ref={sourceAnchorRef} className="bg-bg-1 border border-border-14 rounded-lg p-2">
            <div className="flex items-center gap-1.5 mb-2">
              <STEP_FIELD_ICONS.output className="h-3.5 w-3.5 text-fg-secondary" />
              <span className="text-sm font-medium text-foreground">
                {t('processPage.Output')}
                <span className="ml-1 text-destructive">*</span>
              </span>
            </div>
            <textarea
              className="block w-full p-3 m-0 text-sm border border-line rounded-lg resize-none focus:outline-hidden focus:ring-2 focus:ring-info-solid focus:border-transparent"
              rows={2}
              placeholder={t('viralRecording.labels.defineOutput')}
              ref={output.attachInput}
              value={output.value}
              onFocus={output.handleFocus}
              onBlur={() => {
                output.handleBlur();
                // Same as the description field: flush so validation sees the
                // value at blur.
                data
                  .onFlushPendingChanges()
                  .then(output.markCommitted)
                  .catch(() => {});
              }}
              onChange={(e) => {
                const newValue = e.target.value;
                output.setValue(newValue); // Immediate UI update
                data.onChangeDebounced('task.output', newValue); // Debounced server sync
              }}
            />
          </div>
        </TimelineSection>
      )}
    </div>
  );
};

export default memo(StepCardNode);
